import React, { Fragment, memo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { productApi } from '@app/store';
import { ISchedule, ProductDTO, ScheduleAvailabilityTypeEnum } from '@app/common';
import { Button, Form, Panel } from '@app/components';
import { ProductForm } from './ProductForm';
import {
  defaultProductVariationsFormValues,
  ProductFormValues,
  useProductFormSubmit,
} from './product-form.helpers';
import { useToast, useTriptych } from '@app/hooks';
import { ProductCard } from './ProductCard';
import { InputFileRef } from '@app/components/form/InputFile';
import { useAppSelector } from '@app/store/hooks';
import { triptychSelectors } from '@app/store/slices/triptych.slice';
import type { ValeursDeCopie } from '../Duplication/cles-privees';

type Props = { copie?: ValeursDeCopie<ProductFormValues> };

// Le formulaire porte le useForm : il est remonté (clé) quand on passe d'une copie
// au vierge ou d'une copie à une autre, car react-hook-form ne relit
// `defaultValues` qu'au montage.
const ProductCreateForm = memo(({ copie }: Props) => {
  const toast = useToast();

  const triptych = useTriptych();

  const cardImageRef = useRef<InputFileRef>();

  const onSubmit = useProductFormSubmit();

  const [create, { isLoading, error: apiErrors }] = productApi.useCreateOneProductsMutation();

  const valeursVierges: ProductFormValues = {
    image: null,
    ingredients: [],
    kitchenAreas: [],
    isAlcohol: false,
    name: '',
    description: '',
    disabled: false,
    options: [],
    tags: [],
    allergens: [],
    productVariations: [defaultProductVariationsFormValues],
    isDeclinable: false,
    availabilityType: ScheduleAvailabilityTypeEnum.always,
    availabilitySchedule: {} as ISchedule,
  };

  const defaultValues = copie?.copieDe ? copie : valeursVierges;

  const formMethods = useForm<ProductFormValues>({
    resolver: classValidatorResolver(ProductDTO),
    defaultValues,
  });

  const onCreate = async (formValues: FormData) => {
    try {
      const product = await create(formValues).unwrap();

      triptych.close({ mode: 'UPDATE', data: product });

      toast.success('Votre produit a bien été ajouté.');
    } catch {
      toast.error('Une erreur est survenue lors de la création de votre produit.');
    }
  };

  return (
    <Fragment>
      <ProductCard
        // l'aperçu de l'original reste affiché tant qu'aucun fichier n'est choisi
        product={copie?.copieDe ? { images: copie.imagesDeLOriginal } : undefined}
        formMethods={formMethods}
        toggle={() => triptych.toggle()}
        onOpenImagePicker={() => cardImageRef.current?.open()}
      />

      <Form formMethods={formMethods} apiErrors={apiErrors}>
        <Panel.Content>
          <Panel.FormContainer>
            <ProductForm ref={cardImageRef} />
          </Panel.FormContainer>

          <Panel.Actions>
            <Button title="Retour" grow large bordered onPress={() => triptych.close()} />

            <Button
              title="Valider"
              loading={isLoading}
              onPress={() => onSubmit(formMethods, onCreate)}
              grow
              large
            />
          </Panel.Actions>
        </Panel.Content>
      </Form>
    </Fragment>
  );
});

export const ProductCreate = memo(() => {
  // Dupliquer : la fiche ouvre ce panneau avec les valeurs de copie dans le triptyque.
  const copie = useAppSelector(triptychSelectors.selectData) as ValeursDeCopie<ProductFormValues> | undefined;

  return <ProductCreateForm key={copie?.copieDe ?? 'creation'} copie={copie?.copieDe ? copie : undefined} />;
});
